import React from "react";
import { useCompanion } from "@/companion/CompanionContext";
import { useCompanionAssets } from "@/companion/CompanionAssetsContext";

/**
 * CompanionAvatar — the small "who is speaking" avatar used in dialog
 * headers, tour panels, and inline reassurance messages.
 *
 * This is NOT the PresenceVessel (that is SONMI's ambient surface).
 * Avatars are illustrative — they are the face of the active companion
 * inside conversational surfaces.
 *
 * Renders:
 *   • Toto (profile.avatarKey === "toto") → uploaded artwork if present,
 *     otherwise a minimalist SVG Westie fallback.
 *   • Any companion with `capabilities.animatedPresence === false` and no
 *     avatar assets (Morrow) → a small abstract disc — no companion
 *     imagery of any kind. This preserves the "shared shell contains no
 *     companion-specific assets" invariant.
 */
const AVATAR_RENDERERS = {
  toto: TotoAvatarRenderer,
};

export default function CompanionAvatar({ state = "idle", size = 32, className = "", ...rest }) {
  const profile = useCompanion();
  const Renderer = profile.avatarKey ? AVATAR_RENDERERS[profile.avatarKey] : null;
  if (Renderer) {
    return <Renderer state={state} size={size} className={className} {...rest} />;
  }
  // Neutral placeholder — no imagery, no companion identity. Just a disc
  // that respects the current text color. Morrow uses this by design.
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full ${className}`}
      style={{ width: size, height: size, background: "currentColor", opacity: 0.14 }}
      data-testid={`companion-avatar-${profile.id}-${state}`}
      {...rest}
    />
  );
}

/* ============================================================
 * Toto's avatar renderer — Toto-only artwork lives here.
 * ============================================================ */
function TotoAvatarRenderer({ state, size, className, ...rest }) {
  const { assets } = useCompanionAssets();
  const asset = assets?.[state];

  if (asset?.url) {
    return (
      <span
        className={`toto-avatar toto-state-${state} inline-flex items-center justify-center ${className}`}
        style={{ width: size, height: size }}
        data-testid={`toto-avatar-${state}`}
        {...rest}
      >
        <img
          src={asset.url}
          alt={`Toto ${state}`}
          width={size} height={size}
          style={{ width: size, height: size, objectFit: "contain" }}
          draggable={false}
        />
      </span>
    );
  }
  return <TotoSvgFallback state={state} size={size} className={className} {...rest} />;
}

function TotoSvgFallback({ state, size, className, ...rest }) {
  const s = size;
  const stroke = "currentColor";
  const strokeWidth = Math.max(1, s / 32);
  const baseProps = {
    fill: "none", stroke, strokeWidth,
    strokeLinecap: "round", strokeLinejoin: "round",
    vectorEffect: "non-scaling-stroke",
  };
  return (
    <span
      className={`toto-avatar toto-state-${state} inline-flex items-center justify-center ${className}`}
      style={{ width: s, height: s, color: "inherit" }}
      data-testid={`toto-avatar-${state}`}
      {...rest}
    >
      <svg width={s} height={s} viewBox="0 0 64 64" style={{ overflow: "visible" }}>
        <g className="toto-body">
          <path d="M18 42 Q18 30 26 28 L40 28 Q48 28 50 34 L50 42 Q50 46 46 46 L22 46 Q18 46 18 42 Z" {...baseProps} />
          <path d="M40 28 Q42 20 46 20 Q50 20 50 26 Q56 26 56 32 Q56 36 52 37 L50 34" {...baseProps} />
          <path d="M46 20 L45 15 L48 18 Z" {...baseProps} />
          <path d="M53 26 L54 21 L56 24 Z" {...baseProps} />
          <circle cx="50.5" cy="28.5" r={strokeWidth * 0.7} fill={stroke} />
          <circle cx="55.5" cy="32" r={strokeWidth * 0.8} fill={stroke} />
          <path d="M24 46 L24 52 Q24 54 26 54 L28 54 L28 46" {...baseProps} />
          <path d="M42 46 L42 52 Q42 54 44 54 L46 54 L46 46" {...baseProps} />
          <path d="M42 34 Q45 33 46 30" {...baseProps} />
          <circle cx="45" cy="34" r={strokeWidth * 0.9} fill={stroke} />
        </g>
        <g className="toto-tail">
          <path d="M18 34 Q13 32 12 28" {...baseProps} />
        </g>
        {state === "thinking" && (
          <g className="toto-overlay toto-overlay-thinking">
            <circle cx="58" cy="12" r={strokeWidth * 1.2} {...baseProps} />
            <circle cx="52" cy="16" r={strokeWidth * 0.8} {...baseProps} />
          </g>
        )}
        {state === "helping" && (
          <g className="toto-overlay toto-overlay-helping">
            <rect x="20" y="52" width="18" height="8" rx="1" {...baseProps} />
            <path d="M20 60 L38 60" {...baseProps} />
          </g>
        )}
      </svg>
    </span>
  );
}
